/**
 * Formatting address would be a lot of conditional statements, and even more
 * if we add additional address components and variants in the future. Instead
 * we convert the address to a token-like structure that represents the order
 * and behaviour of address components and then serialize it to text.
 */
import { omitBy, trim } from 'lodash';
import { IMask } from 'react-imask';

import { Address, Recipient, SearchAddress } from 'api/api';

import {
  AddressPartName,
  getAddressTemplateConfig,
  getPresentationalTemplateConfig,
} from '../config/template-config';
import { getPostalCodeFieldConfig } from '../validation/postal-code-validation';

export type ExternalAddress = Partial<Address | SearchAddress>;

export interface AddressFormatOptions {
  /**
   * "country" should be mandatory here, because the one included in address
   * might be missing or invalid.
   */
  country: string;
  first_address_line_only?: boolean; // format first address_line
  multiline?: boolean;
  skip_postal_code_format?: boolean; // this is for unit test only.
  show_street?: boolean;
  show_city?: boolean;
  show_postal_code?: boolean;
  show_region?: boolean;
  presentational?: boolean;
}

export interface StructuredAddressToken {
  name: AddressPartName;
  value: string;
}

export type AddressToken = StructuredAddressToken | string;

export function formatAddress(address: ExternalAddress, options: AddressFormatOptions): string {
  // Traditionally we just expect the address to be one line only.
  return formatAddressToArray(address, options)[0];
}

export function formatAddressToArray(
  address: ExternalAddress,
  options: AddressFormatOptions
): string[] {
  const tokens = formatAddressToTokens(address, options);
  return serializeAddressTokensToArray(tokens);
}

export function formatAddressToTokens(
  address: ExternalAddress,
  options: AddressFormatOptions
): AddressToken[] {
  const formattedAddress = preformatAddress(address, options);
  return generateAddressTokens(formattedAddress, options);
}

/**
 * format separate address fields in isolation.
 */
export function preformatAddress(address: ExternalAddress, options: AddressFormatOptions) {
  const postalCodeConfig = getPostalCodeFieldConfig(options.country);

  const maskPipe = IMask.createPipe({
    mask: postalCodeConfig.mask,
  });

  const dedupeAddressLines =
    address.address_lines !== undefined &&
    ((address as Address).street || (address as Address).street_number);

  return {
    ...address,
    postal_code: options.skip_postal_code_format
      ? address.postal_code
      : maskPipe(address.postal_code || ''),
    address_lines: dedupeAddressLines ? address.address_lines!.slice(1) : address.address_lines,
  };
}

function isAddressPartVisible(name: string, options: AddressFormatOptions) {
  if (options.first_address_line_only) {
    // special case, we use the template to format first address line.
    switch (name) {
      case 'street':
      case 'street_number':
      case 'apartment_number':
        return true;
      default:
        return false;
    }
  }

  switch (name) {
    case 'street':
    case 'street_number':
    case 'apartment_number':
    case 'address_line_1':
    case 'address_line_2':
      return options.show_street;
    case 'postal_code':
      return options.show_postal_code;
    case 'city':
      return options.show_city;
    case 'region_code':
      return options.show_region;
    case 'comma':
      return true;
    default:
      return false;
  }
}

function getAddressPart(address: ExternalAddress, name: AddressPartName) {
  const { address_lines } = address;

  if (name === 'address_line_1') {
    return address_lines ? address_lines[0] : undefined;
  }
  if (name === 'address_line_2') {
    return address_lines ? address_lines[1] : undefined;
  }
  if (name === 'region_code') {
    return address.region;
  }

  return (address as Address)[name];
}

/**
 * structure tokens according to country template and insert punctuation.
 */
export function generateAddressTokens(address: ExternalAddress, options: AddressFormatOptions) {
  const template = options.presentational
    ? getPresentationalTemplateConfig(options.country)
    : getAddressTemplateConfig(options.country);

  const tokens: AddressToken[] = [];

  let filled = false;
  let emitComma = false;

  for (const name of template) {
    if (name === 'comma' || name === 'newline') {
      if (!filled) {
        // interpunction at the beginning of sentence, nothing to do.
        continue;
      }

      // if not multiline, treat newline as comma.
      if (name === 'newline' && options.multiline) {
        tokens.push('\n');
        filled = false;
        emitComma = false;
        continue;
      }

      // comma in the middle of sentence -> emit only if we encounter a valid word later.
      emitComma = true;
      continue;
    }

    const value = getAddressPart(address, name);

    if (value === undefined || trim(value) === '' || !isAddressPartVisible(name, options)) {
      // skip undefined, empty, or invisible token
      continue;
    }

    if (emitComma) {
      // possible preceding comma
      emitComma = false;
      tokens.push(',');
    }

    if (filled) {
      // emit preceding whitespace
      tokens.push(' ');
    }

    // valid address part
    tokens.push({ name, value });
    filled = true;
  }

  return tokens;
}

export function serializeAddressTokens(tokens: AddressToken[]): string {
  return tokens
    .map(token => {
      if (typeof token === 'string') {
        return token;
      }
      return token.value;
    })
    .join('');
}

export function serializeAddressTokensToArray(tokens: AddressToken[]): string[] {
  const lines: string[] = [];

  let startIdx = 0;
  while (startIdx <= tokens.length) {
    let endIdx = tokens.findIndex((token, idx) => token === '\n' && idx >= startIdx);
    if (endIdx === -1) {
      endIdx = tokens.length + 1;
    }
    lines.push(serializeAddressTokens(tokens.slice(startIdx, endIdx)));
    startIdx = endIdx + 1;
  }

  return lines;
}

/**
 * Format address_lines based on street and street_number.
 */
export function complementAddress(address: Address, country: string) {
  if (!address.street && !address.street_number) {
    return address;
  }

  const address_lines =
    address.address_lines !== undefined ? Array.from(address.address_lines) : [];

  address_lines[0] = formatAddress(address, {
    country,
    first_address_line_only: true,
  });

  return {
    ...address,
    address_lines,
  };
}

export function trimEmptyAdditionalData(address: Recipient) {
  /**
   * We're removing "additional" address fields from the object
   * when their value is falsy.
   * For now, the only used one in the address form is the VAT field
   *
   * TODO: Add the rest when requested (f.e. care_of)
   */

  const optionalFields = ['vat'];

  return omitBy(address, (v, k) => optionalFields.includes(k) && !v);
}
